<template>
    <div>
        <div class="table-controls | u-text-right">
            <div class="page-block__header">
                <h1 class="tabulator__title">{{ trans('page.users.title') }}</h1>
                <div>
                    <customer-router-link
                        :to="{ name: 'usersCreate' }"
                        class="btn btn--ghost btn--small | u-margin-right-narrow"
                        dusk="create-user-button"
                    >
                        {{ trans('action.add') }}
                    </customer-router-link>
                    <button class="btn btn--ghost btn--small" @click="downloadCsv">
                        {{ trans('action.download.csv') }}
                    </button>
                </div>
            </div>
        </div>
        <div ref="table"></div>
        <confirmation-modal
            v-if="confirmEntity"
            :confirmEntity="confirmEntity"
            @confirm="confirmRemoval"
            @close="cancelRemoval"
        />
    </div>
</template>

<script>
import UsersApi from '../services/api/user.js';
import { EventBus } from 'relapp-event';
import Tabulator from 'tabulator-tables';
import userMixin from '../mixins/user.js';
import { ConfirmationModal } from 'relapp-confirm';

import '../styles/_tabulator.scss';

export default {
    components: { ConfirmationModal },
    mixins: [userMixin],
    props: {
        tableData: {
            type: Array,
            required: true
        }
    },
    computed: {
        /**
         * Returns confirmation text placeholder text.
         *
         * @return {string}
         */
        confirmEntity() {
            if (!this.usersToDelete) {
                return null;
            }

            return this.usersToDelete.map(user => user.name).join(', ');
        }
    },
    /**
     * Add all the properties found in the data object to Vue’s reactivity system.
     *
     * @return {object}
     */
    data() {
        return {
            usersToDelete: [],
            tabulator: null,
            columns: [
                {
                    title: trans('user.first_name'),
                    field: 'first_name',
                    headerFilter: 'input',
                    headerFilterPlaceholder: trans('action.search')
                },
                {
                    title: trans('user.last_name'),
                    field: 'last_name',
                    headerFilter: 'input',
                    headerFilterPlaceholder: trans('action.search')
                },
                {
                    title: trans('user.email'),
                    field: 'email',
                    headerFilter: 'input',
                    headerFilterPlaceholder: trans('action.search')
                },
                {
                    title: trans('user.role'),
                    field: 'role',
                    headerFilter: 'select',
                    headerFilterParams: { values: true },
                    headerFilterPlaceholder: trans('action.filter')
                },
                {
                    formatter: this.removeButtonFormatter,
                    width: 70,
                    headerSort: false,
                    align: 'center',
                    cellClick: (e, cell) => this.removeCellClick(cell)
                }
            ]
        };
    },
    watch: {
        tableData: {
            /**
             * Watcher for the tableData
             *
             * @param {array} newData
             */
            handler(newData) {
                this.tabulator.replaceData(newData);
                this.tabulator.redraw(true);
            },
            deep: true
        }
    },
    /**
     * Called after the instance has been mounted.
     */
    mounted() {
        this.tabulator = new Tabulator(this.$refs.table, {
            columns: this.columns,
            data: this.tableData,
            layout: 'fitColumns',
            responsiveLayout: 'collapse',
            resizableColumns: false,
            selectable: false
        });
    },
    methods: {
        /**
         * Action when a delete icon is clicked
         * @param  {CellComponent} cell
         */
        removeCellClick(cell) {
            if (cell.getRow().getData().id !== this.currentUser.id) {
                this.usersToDelete.push(cell.getRow().getData());
            }
        },
        /**
         * Same functionality as the buttonCross formatter, except the extra check to see if the user is
         * the current user.
         * @param  {CellComponent} cell
         * @return {string}
         */
        removeButtonFormatter(cell) {
            if (cell.getRow().getData().id === this.currentUser.id) {
                cell.getRow().getData().currentClient = true;

                return '';
            }
            cell.getRow().getData().currentClient = false;

            return '<div class="product__item-delete | u-align-center"><div class="tabulator-delete" /></div>';
        },
        /**
         * Confirmation to remove users is confirmed. Now we actually remove the users.
         */
        confirmRemoval() {
            this.usersToDelete.forEach(this.deleteUser);
            this.usersToDelete = [];
        },

        /**
         * Cancels to remove a few users.
         */
        cancelRemoval() {
            this.usersToDelete = [];
        },

        /**
         * Download the contents of the table in CSV format.
         */
        downloadCsv() {
            this.tabulator.download('csv', 'data.csv');
        },
        /**
         * Does API call to remove an user.
         *
         * @param {object} user
         *
         * @return {Promise}
         */
        deleteUser(user) {
            return UsersApi.delete(user)
                .then(() => {
                    EventBus.$emit('notification', {
                        type: 'success',
                        message: trans('action.deleted')
                    });
                })
                .catch(this.handleError);
        }
    }
};
</script>
